// Centrifuge run file: a self-contained JSON snapshot of one centrifuge test —
// the peak result plus the decoded waveform records — so a run can be saved to
// disk and re-imported later to review. Round-trips through buildCentrifugeFile
// / parseCentrifugeFile.

import { t } from "i18n";

export const CENTRIFUGE_FILE_FORMAT = "sit.centrifuge";
export const CENTRIFUGE_FILE_VERSION = 1;

const fieldLabelDisplay = (w) => {
  if (w?.field_label_display) return w.field_label_display;
  if (w?.field_label != null) return String(w.field_label).padStart(3, "0");
  return null;
};

// Strip characters that are awkward or illegal in filenames across OSes, and
// collapse whitespace so the name stays tidy.
const sanitize = (s) =>
  String(s || "")
    .replace(/[/\\?%*:|"<>]/g, "")
    .replace(/\s+/g, " ")
    .trim();

// A short human identity for the device: prefer the printed label, then serial,
// then MAC. `translate` is false for file names (machine-readable, always
// English); the displayed name goes through i18n.
const shortName = (wearable, fallbackMac, translate) => {
  const label = fieldLabelDisplay(wearable);
  if (label) {
    return translate
      ? t("utils.centrifugeFile.unitName", { label })
      : `Unit ${label}`;
  }
  if (wearable?.serial) return wearable.serial;
  const mac = wearable?.ble_mac || fallbackMac;
  if (mac) return String(mac).toUpperCase();
  return translate ? t("utils.centrifugeFile.wearableFallback") : "wearable";
};

// Displayed name (imported-run banner, review sources).
export const wearableShortName = (wearable, fallbackMac) =>
  shortName(wearable, fallbackMac, true);

// Untranslated variant for file names.
export const wearableFileShortName = (wearable, fallbackMac) =>
  shortName(wearable, fallbackMac, false);

// A readable default filename, e.g.
// "Capture run - Unit 002 - 210g - 2026-07-31.json".
// `date` is passed in (callers stamp it) so this stays pure and testable.
export const centrifugeFileName = ({ wearable, mac, peakG, date }) => {
  const who = sanitize(wearableFileShortName(wearable, mac));
  const day = date instanceof Date ? date.toISOString().slice(0, 10) : date;
  const peak = peakG != null ? ` - ${Math.round(peakG)}g` : "";
  return sanitize(`Capture run - ${who}${peak} - ${day}`) + ".json";
};

// Build the file payload (object + pretty JSON string). `exportedAt` is passed
// in so the function is pure.
export const buildCentrifugeFile = ({
  wearable,
  mac,
  fw,
  peak,
  note,
  records,
  exportedAt,
}) => {
  const payload = {
    format: CENTRIFUGE_FILE_FORMAT,
    version: CENTRIFUGE_FILE_VERSION,
    exported_at: exportedAt,
    wearable: {
      ble_mac: wearable?.ble_mac || mac || null,
      serial: wearable?.serial || null,
      field_label: wearable?.field_label ?? null,
      field_label_display: fieldLabelDisplay(wearable),
    },
    firmware: fw || null,
    peak: peak
      ? {
          g: peak.peakG ?? null,
          records: peak.records ?? null,
          source: peak.source || null,
        }
      : null,
    note: note || "",
    records: records || [],
  };
  return { payload, json: JSON.stringify(payload, null, 2) };
};

// Parse + validate a centrifuge file's text. Throws with an actionable message
// if it isn't one of ours.
export const parseCentrifugeFile = (text) => {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(t("utils.centrifugeFile.invalidJson"));
  }
  if (!data || data.format !== CENTRIFUGE_FILE_FORMAT) {
    throw new Error(t("utils.centrifugeFile.notCentrifugeFile"));
  }
  return {
    wearable: data.wearable || null,
    firmware: data.firmware || null,
    peak: data.peak
      ? {
          peakG: data.peak.g ?? null,
          records: data.peak.records ?? null,
          source: data.peak.source || "file",
        }
      : null,
    note: data.note || "",
    records: Array.isArray(data.records) ? data.records : [],
    exportedAt: data.exported_at || null,
  };
};
